import { createSlice } from "@reduxjs/toolkit";

// Navigation slice for managing form navigation state
const navigationSlice = createSlice({
    name: 'navigation',
    initialState: {
        // Recent forms accessed
        recentForms: [],
        // Recent search terms
        recentSearches: [],
        // User preferences
        preferences: {
            keyboardShortcuts: true,
            defaultFolderView: 'all',
            formsPerPage: 12,
            compactView: false
        },
        // Form access statistics
        formAccessStats: {},
        // Navigation history for breadcrumbs
        navigationHistory: [],
        // Quick access pins
        pinnedForms: [],
        // Multi-patient context with form tracking
        multiPatientContext: {
            activePatients: [],
            currentPatientId: null,
            maxPatients: 5,
            recentPatients: [], // Recently accessed patients
            switchHistory: [] // Patient switch history for quick access
        },
        // Active forms per patient
        activeForms: {},
        // Current form being viewed
        currentForm: null,
        // Form states for persistence
        formStates: {}
    },
    reducers: {
        // Add form to recent forms
        addRecentForm: (state, action) => {
            const form = action.payload;
            const existingIndex = state.recentForms.findIndex(
                f => (f.form_id || f.id) === (form.form_id || form.id)
            );
            
            if (existingIndex >= 0) {
                // Move to front if already exists
                state.recentForms.splice(existingIndex, 1);
            }
            
            // Add to front and limit to 10 recent forms
            state.recentForms.unshift({
                ...form,
                lastUsed: new Date().toISOString()
            });
            
            if (state.recentForms.length > 10) {
                state.recentForms = state.recentForms.slice(0, 10);
            }
        },

        // Add search term to recent searches
        addRecentSearch: (state, action) => {
            const searchTerm = action.payload.trim().toLowerCase();
            if (!searchTerm || searchTerm.length < 2) return;
            
            const existingIndex = state.recentSearches.indexOf(searchTerm);
            if (existingIndex >= 0) {
                state.recentSearches.splice(existingIndex, 1);
            }
            
            state.recentSearches.unshift(searchTerm);
            
            if (state.recentSearches.length > 5) {
                state.recentSearches = state.recentSearches.slice(0, 5);
            }
        },

        // Update user preferences
        updatePreferences: (state, action) => {
            state.preferences = { ...state.preferences, ...action.payload };
        },

        // Track form access for analytics
        trackFormAccess: (state, action) => {
            const { formId, timestamp = new Date().toISOString() } = action.payload;
            
            if (!state.formAccessStats[formId]) {
                state.formAccessStats[formId] = {
                    count: 0,
                    lastAccessed: null,
                    firstAccessed: timestamp
                };
            }
            
            state.formAccessStats[formId].count += 1;
            state.formAccessStats[formId].lastAccessed = timestamp;
        },

        // Add navigation breadcrumb
        addNavigationHistory: (state, action) => {
            const { path, label, timestamp = new Date().toISOString() } = action.payload;
            
            state.navigationHistory.push({
                path,
                label,
                timestamp
            });
            
            // Keep only last 20 navigation items
            if (state.navigationHistory.length > 20) {
                state.navigationHistory = state.navigationHistory.slice(-20);
            }
        },

        // Clear navigation history
        clearNavigationHistory: (state) => {
            state.navigationHistory = [];
        },

        // Pin/unpin forms for quick access
        togglePinnedForm: (state, action) => {
            const form = action.payload;
            const formId = form.form_id || form.id;
            const existingIndex = state.pinnedForms.findIndex(
                f => (f.form_id || f.id) === formId
            );
            
            if (existingIndex >= 0) {
                // Unpin
                state.pinnedForms.splice(existingIndex, 1);
            } else {
                // Pin (limit to 8 pinned forms)
                if (state.pinnedForms.length < 8) {
                    state.pinnedForms.push({
                        ...form,
                        pinnedAt: new Date().toISOString()
                    });
                }
            }
        },

        // Multi-patient context management (Phase 2)
        addPatientToContext: (state, action) => {
            const patient = action.payload;
            const existingIndex = state.multiPatientContext.activePatients.findIndex(
                p => p.id === patient.id
            );
            
            if (existingIndex >= 0) {
                // Update existing patient
                state.multiPatientContext.activePatients[existingIndex] = patient;
            } else {
                // Add new patient
                if (state.multiPatientContext.activePatients.length < state.multiPatientContext.maxPatients) {
                    state.multiPatientContext.activePatients.push({
                        ...patient,
                        addedAt: new Date().toISOString(),
                        formState: {}
                    });
                } else {
                    // Remove oldest patient and add new one
                    state.multiPatientContext.activePatients.shift();
                    state.multiPatientContext.activePatients.push({
                        ...patient,
                        addedAt: new Date().toISOString(),
                        formState: {}
                    });
                }
            }
        },

        // Remove patient from context
        removePatientFromContext: (state, action) => {
            const patientId = action.payload;
            state.multiPatientContext.activePatients = state.multiPatientContext.activePatients.filter(
                p => p.id !== patientId
            );
            
            // Update current patient if removed
            if (state.multiPatientContext.currentPatientId === patientId) {
                state.multiPatientContext.currentPatientId = 
                    state.multiPatientContext.activePatients.length > 0 
                        ? state.multiPatientContext.activePatients[0].id 
                        : null;
            }
        },

        // Set current patient
        setCurrentPatient: (state, action) => {
            const patientId = action.payload;
            const patientExists = state.multiPatientContext.activePatients.some(p => p.id === patientId);
            
            if (patientExists) {
                const previousPatientId = state.multiPatientContext.currentPatientId;
                state.multiPatientContext.currentPatientId = patientId;
                
                // Add to switch history
                if (previousPatientId && previousPatientId !== patientId) {
                    const switchRecord = {
                        from: previousPatientId,
                        to: patientId,
                        timestamp: new Date().toISOString()
                    };
                    
                    state.multiPatientContext.switchHistory.unshift(switchRecord);
                    
                    // Keep only last 10 switches
                    if (state.multiPatientContext.switchHistory.length > 10) {
                        state.multiPatientContext.switchHistory = state.multiPatientContext.switchHistory.slice(0, 10);
                    }
                }
                
                // Update recent patients
                const currentPatient = state.multiPatientContext.activePatients.find(p => p.id === patientId);
                if (currentPatient) {
                    // Remove from current position if exists
                    state.multiPatientContext.recentPatients = state.multiPatientContext.recentPatients.filter(p => p.id !== patientId);
                    
                    // Add to front
                    state.multiPatientContext.recentPatients.unshift({
                        ...currentPatient,
                        lastAccessed: new Date().toISOString()
                    });
                    
                    // Keep only last 5 recent patients
                    if (state.multiPatientContext.recentPatients.length > 5) {
                        state.multiPatientContext.recentPatients = state.multiPatientContext.recentPatients.slice(0, 5);
                    }
                }
            }
        },

        // Save form state for patient (for context switching)
        savePatientFormState: (state, action) => {
            const { patientId, formId, formState } = action.payload;
            const patientIndex = state.multiPatientContext.activePatients.findIndex(p => p.id === patientId);
            
            if (patientIndex >= 0) {
                if (!state.multiPatientContext.activePatients[patientIndex].formState) {
                    state.multiPatientContext.activePatients[patientIndex].formState = {};
                }
                state.multiPatientContext.activePatients[patientIndex].formState[formId] = formState;
            }
        },

        // Get form state for patient
        getPatientFormState: (state, action) => {
            const { patientId, formId } = action.payload;
            const patient = state.multiPatientContext.activePatients.find(p => p.id === patientId);
            return patient?.formState?.[formId] || null;
        },

        // Clear all navigation data
        clearNavigationData: (state) => {
            state.recentForms = [];
            state.recentSearches = [];
            state.formAccessStats = {};
            state.navigationHistory = [];
            state.pinnedForms = [];
        },

        // Reset multi-patient context
        resetMultiPatientContext: (state) => {
            state.multiPatientContext = {
                activePatients: [],
                currentPatientId: null,
                maxPatients: 5,
                recentPatients: [],
                switchHistory: []
            };
        },

        // Active forms management
        addActiveForm: (state, action) => {
            const { patientId, form } = action.payload;
            if (!state.activeForms[patientId]) {
                state.activeForms[patientId] = [];
            }
            const existingForm = state.activeForms[patientId].find(f => f.id === form.id);
            if (!existingForm) {
                state.activeForms[patientId].push({
                    ...form,
                    hasUnsavedChanges: false,
                    openedAt: new Date().toISOString()
                });
            }
        },

        removeActiveForm: (state, action) => {
            const { patientId, formId } = action.payload;
            if (state.activeForms[patientId]) {
                state.activeForms[patientId] = state.activeForms[patientId].filter(f => f.id !== formId);
            }
        },

        setCurrentForm: (state, action) => {
            state.currentForm = action.payload;
        },

        updateFormUnsavedStatus: (state, action) => {
            const { patientId, formId, hasUnsavedChanges } = action.payload;
            if (state.activeForms[patientId]) {
                const form = state.activeForms[patientId].find(f => f.id === formId);
                if (form) {
                    form.hasUnsavedChanges = hasUnsavedChanges;
                }
            }
        },

        // Enhanced form state management
        setFormState: (state, action) => {
            const { patientId, formId, formData } = action.payload;
            const key = `${patientId}_${formId}`;
            state.formStates[key] = {
                ...formData,
                lastModified: new Date().toISOString()
            };
        },

        getFormState: (state, action) => {
            const { patientId, formId } = action.payload;
            const key = `${patientId}_${formId}`;
            return state.formStates[key] || null;
        },

        clearFormState: (state, action) => {
            const { patientId, formId } = action.payload;
            const key = `${patientId}_${formId}`;
            delete state.formStates[key];
        }
    }
});

export const {
    addRecentForm,
    addRecentSearch,
    updatePreferences,
    trackFormAccess,
    addNavigationHistory,
    clearNavigationHistory,
    togglePinnedForm,
    addPatientToContext,
    removePatientFromContext,
    setCurrentPatient,
    savePatientFormState,
    getPatientFormState,
    clearNavigationData,
    resetMultiPatientContext,
    addActiveForm,
    removeActiveForm,
    setCurrentForm,
    updateFormUnsavedStatus,
    setFormState,
    getFormState,
    clearFormState
} = navigationSlice.actions;

export default navigationSlice.reducer;